// Матч-центр (вкладка matches, И-1.2) — экран в экран как MLS iOS (MatchCenterScreen): шапка оболочки (LIVE,
// календарь, поиск → «Скоро», меню — config.navigation.headers.matches, src/ui/AppBar.tsx), под ней полоса дней ±6,
// список дня: блок топ-лиг → страны (порядок ответа, sections.ts), пустой день — empty_state сервера, загрузка —
// скелетон, 5xx/429/нет сети — состояния оболочки.
// Опрос: день — по poll.interval_seconds, живой батч — по live_batch_interval_seconds, пока на экране есть живой матч;
// в фоне стоят оба (src/api/queryClient.ts). Переход на матч — /match/:id; BackButton возвращает сюда на тот же день,
// с той же прокруткой и LIVE (store.ts, headerState).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../api/config';
import { ApiError, apiGet } from '../../api/client';
import { dayKey, dayUrl, featuredOf, syncOf, timezoneOffset, useLiveBatch, useMatchDay, type MatchDayResponse } from '../../api/matchDay';
import { t } from '../../i18n/i18n';
import { paths } from '../../nav';
import trophy from '../../assets/icons/trophy.svg';
import { setHeaderState, useHeaderState } from '../../ui/headerState';
import { MaskIcon } from '../../ui/Icon';
import { ErrorView, StateView } from '../../ui/States';
import { CalendarSheet } from './CalendarSheet';
import { addDays, localDateKey } from './days';
import { DayStrip } from './DayStrip';
import { liveField, liveIds, mergeLiveBatch } from './live';
import { buildSections, isExpanded, type LeagueView, type SectionView } from './sections';
import { MatchCenterSkeleton, SectionCard, type SectionHandlers } from './Sections';
import { savedScroll, saveScroll, selectDay, toggleSection, useMatchCenterState } from './store';
import styles from './MatchCenter.module.css';

/** Сегодня по часам устройства; переключается в полночь и при возврате в приложение. */
function useToday(): string {
  const [today, setToday] = useState(() => localDateKey(new Date()));
  useEffect(() => {
    const check = () => setToday(localDateKey(new Date()));
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() - now.getTime();
    const timer = setTimeout(check, midnight + 1000);
    document.addEventListener('visibilitychange', check);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', check); };
  }, [today]);
  return today;
}

export default function MatchCenterScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const config = useConfig();
  const sync = syncOf(config.data);
  const featured = featuredOf(config.data);
  const header = useHeaderState();
  const mc = useMatchCenterState();
  const today = useToday();
  const date = mc.date ?? today;
  const tz = useMemo(() => timezoneOffset(), [date]); // пояс устройства на момент выбора дня
  const day = useMatchDay(sync, date, tz);
  const field = liveField(sync.live_filter_field, header.liveField);

  const ids = useMemo(() => liveIds(day.data, field), [day.data, field]);
  const batch = useLiveBatch({ sync, poll: day.data?.poll, date, tz, ids, enabled: ids.length > 0, dayUpdatedAt: day.dataUpdatedAt });
  const merged = useMemo<MatchDayResponse | undefined>(
    () => day.data && mergeLiveBatch(day.data, batch.data?.results, batch.dataUpdatedAt < day.dataUpdatedAt),
    [day.data, day.dataUpdatedAt, batch.data, batch.dataUpdatedAt]);
  const sections = useMemo(() => buildSections(merged, { liveOnly: header.liveOnly, field, topTitle: t('matchCenter.topLeagues') }),
    [merged, header.liveOnly, field]);

  // соседние дни — фоном, как iOS (переход на «Вчера/Завтра» без скелетона; запрос при тапе всё равно уходит)
  useEffect(() => {
    if (!day.data) return;
    for (const d of [addDays(date, -1), addDays(date, 1)]) {
      void queryClient.prefetchQuery({ queryKey: dayKey(d, tz), queryFn: () => apiGet<MatchDayResponse>(dayUrl(sync, d, tz)), staleTime: 60_000 });
    }
  }, [day.data, date, tz, sync, queryClient]);

  // шапка уезжает при прокрутке вниз (вместе с таббаром), полоса дней поднимается к верху
  useEffect(() => {
    document.documentElement.toggleAttribute('data-collapsing-header', true);
    return () => document.documentElement.removeAttribute('data-collapsing-header');
  }, []);

  // прокрутка переживает экран матча: пишем, пока смонтированы (layout-эффект — снимаем до того, как DOM уйдёт)
  useLayoutEffect(() => {
    const onScroll = () => saveScroll(date, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [date]);
  const restored = useRef(false);
  const hasList = sections.length > 0;
  useLayoutEffect(() => {
    if (restored.current || !day.data) return;
    restored.current = true;
    const y = savedScroll(date);
    if (y) window.scrollTo(0, y);
  }, [day.data, hasList, date]);

  const pickDay = useCallback((d: string) => {
    selectDay(d === today ? null : d);
    setHeaderState({ calendarOpen: false });
    window.scrollTo(0, 0);
  }, [today]);
  const closeCalendar = useCallback(() => setHeaderState({ calendarOpen: false }), []);

  const handlers = useMemo<SectionHandlers>(() => ({
    onToggle: (s: SectionView, expanded: boolean) => toggleSection(date, s.key, expanded),
    onMatch: (id: number) => navigate(paths.match(id)),
    onLeague: (l: LeagueView) => navigate(paths.league(l.id, l.name || undefined)),
  }), [date, navigate]);

  const countries = sections.filter((s) => !s.top).length;
  const toggled = mc.toggled[date] ?? {};

  let body;
  if (day.isPending) {
    // без сети запрос стоит на паузе, первая попытка не удалась (5xx/429 — повторы идут) — ошибка, а не вечный скелетон
    const offline = day.fetchStatus === 'paused';
    body = offline || day.failureCount > 0
      ? <ErrorView error={offline ? new ApiError('network', 0, null) : day.failureReason} onRetry={() => void day.refetch()} />
      : <MatchCenterSkeleton />;
  } else if (day.isError) {
    body = <ErrorView error={day.error} onRetry={() => void day.refetch()} />;
  } else if (!hasList) {
    body = <EmptyDay day={merged} liveOnly={header.liveOnly} onPickDate={() => setHeaderState({ calendarOpen: true })}
      onReload={() => void day.refetch()} />;
  } else {
    body = (
      <div className={styles.list}>
        {sections.map((s) => (
          <SectionCard key={s.key} section={s} handlers={handlers}
            expanded={isExpanded(s, { toggled, liveOnly: header.liveOnly, countries })} />
        ))}
      </div>
    );
  }

  return (
    <>
      <DayStrip today={today} selected={date} onSelect={pickDay} />
      {body}
      {featured?.available && <FeaturedButton label={featured.tab_label || featured.tab_title || featured.name_localized || t('matchCenter.featured')}
        icon={featured.tab_icon_url} onOpen={() => navigate(paths.league(featured.league_id ?? '', featured.name_localized || featured.tab_title))} />}
      {header.calendarOpen && <CalendarSheet today={today} selected={date} leagues={sync.top_league_ids ?? []}
        onPick={pickDay} onClose={closeCalendar} />}
    </>
  );
}

/** Пусто. День без матчей — слова и кнопка сервера (empty_state: pick_date — календарь, reload — перечитать).
 *  Пусто из-за LIVE — сервер про наш фильтр не знает: свои слова и «Показать все матчи». */
function EmptyDay({ day, liveOnly, onPickDate, onReload }: {
  day: MatchDayResponse | undefined; liveOnly: boolean; onPickDate: () => void; onReload: () => void;
}) {
  const server = day?.empty_state;
  if (server && (!liveOnly || !day?.match_count)) {
    const action = server.action;
    const onAction = action?.kind === 'pick_date' ? onPickDate : action?.kind === 'reload' ? onReload : null;
    return (
      <div className={styles.empty}>
        <StateView art="empty" title={server.title} text={server.text}>
          {action && onAction && <button className={styles.button} onClick={onAction}>{action.label}</button>}
        </StateView>
      </div>
    );
  }
  return (
    <div className={styles.empty}>
      {liveOnly
        ? <StateView art="empty" title={t('matchCenter.liveEmptyTitle')} text={t('matchCenter.liveEmptyText')}>
          <button className={styles.button} onClick={() => setHeaderState({ liveOnly: false })}>{t('matchCenter.showAll')}</button>
        </StateView>
        : <StateView art="empty" title={t('matchCenter.noMatches')} />}
    </div>
  );
}

function FeaturedButton({ label, icon, onOpen }: { label: string; icon?: string; onOpen: () => void }) {
  return (
    <button className={styles.featured} onClick={onOpen}>
      {icon ? <img src={icon} alt="" /> : <MaskIcon src={trophy} size={22} />}
      <span className={styles.featuredLabel}>{label}</span>
    </button>
  );
}
